import {
  trackPianoPlayerEvent,
  setPianoAnalyticsMapperConfiguration,
} from '~/services/analytics/piano-analytics';
import type { PlayerTrackData } from '../tracking.ts';

interface PianoAnalyticsPluginOptions {
  trackData: PlayerTrackData;
  playableItem: any;
  enabled?: boolean;
  eventMapperConfig?: string;
}

function pianoAnalyticsPlugin(this: any, options: PianoAnalyticsPluginOptions) {
  if (!options.enabled) {
    return;
  }

  const { trackData, playableItem, eventMapperConfig } = options;

  // Set mapper configuration if provided
  if (eventMapperConfig) {
    try {
      setPianoAnalyticsMapperConfiguration(JSON.parse(eventMapperConfig));
    } catch (error) {
      console.error('Error parsing eventMapperConfig:', error);
    }
  }
  // Piano Analytics specific properties
  const pianoAnalyticsProperties = {
    environment: 'web',
    av_content: playableItem?.title || trackData.name,
    av_content_id: playableItem?.id || trackData.media_id,
    av_content_duration:
      playableItem?.extensions?.duration || trackData?.duration,
    av_content_type: trackData?.free_or_paid,
    player_type: 'videojs',
    media_type: trackData.media_type,
  };

  const handlePianoAnalyticsEvent = (eventName: string, eventData: any) => {
    trackPianoPlayerEvent(eventName, {
      ...eventData,
      ...pianoAnalyticsProperties,
      time: this.currentTime(),
    });
  };

  // Map player events to Piano Analytics event names
  const eventHandlers: Record<string, (this: any) => void> = {
    loadedmetadata: () => {
      handlePianoAnalyticsEvent('player_session_start', trackData);
    },
    play: () => {
      handlePianoAnalyticsEvent('player_play', trackData);
    },
    pause: () => {
      handlePianoAnalyticsEvent('player_pause', trackData);
    },
    ended: () => {
      handlePianoAnalyticsEvent('player_complete', trackData);
    },
    error: () => {
      handlePianoAnalyticsEvent('player_error', trackData);
    },
    seeking: () => {
      handlePianoAnalyticsEvent('player_seek_start', trackData);
    },
    seeked: () => {
      handlePianoAnalyticsEvent('player_seek_complete', trackData);
    },
    waiting: () => {
      handlePianoAnalyticsEvent('player_buffer_start', trackData);
    },
    canplaythrough: () => {
      handlePianoAnalyticsEvent('player_buffer_complete', trackData);
    },
    loadeddata: () => {
      handlePianoAnalyticsEvent('player_presented', trackData);
    },
    volumechange: () => {
      handlePianoAnalyticsEvent('player_volume_change', trackData);
    },
    ratechange: () => {
      handlePianoAnalyticsEvent('player_speed_change', trackData);
    },
    fullscreenchange: () => {
      if (this.isFullscreen()) {
        handlePianoAnalyticsEvent('player_fullscreen_on', trackData);
      } else {
        handlePianoAnalyticsEvent('player_fullscreen_off', trackData);
      }
    },
    // Add more mappings as needed for your player and analytics needs
    // e.g., subtitle events, ad events, etc.
  };

  function onClose(this: any) {
    handlePianoAnalyticsEvent('player_session_end', trackData);
    handlePianoAnalyticsEvent('player_closed', trackData);
    removeListeners();
  }

  const removeListeners = () => {
    Object.keys(eventHandlers).forEach((event) => {
      this.off(event, eventHandlers[event]);
    });
    this.off('dispose', onClose);
  };

  const addListeners = () => {
    removeListeners();
    Object.keys(eventHandlers).forEach((event) => {
      this.on(event, eventHandlers[event]);
    });
    this.on('dispose', onClose);
  };

  addListeners();
}
export default pianoAnalyticsPlugin;
